import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import DOMPurify from 'dompurify';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import ErrorHandler from '@Root/ErrorHandler';
import {
    IoWarning,
    IoTrashBin,
    IoMail,
    IoCheckmarkCircle,
    IoCloseCircle,
    IoAlertCircle,
    IoFolderOpen,
    IoPeople,
    IoBusiness  // Changed from IoBuilding
} from 'react-icons/io5';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './DeleteAccountPage.scss';

export default function DeleteAccountPage(props) {
    const [deleteText, setDeleteText] = useState('');
    const [emailId, setEmailId] = useState('');
    const [deleteOptionDisabled, setDeleteOptionDisabled] = useState(true);
    const [projectOwnerPermissions, setProjectOwnerPermissions] = useState([]);
    const [teamOwnerPermissions, setTeamOwnerPermissions] = useState([]);
    const [orgOwnerPermissions, setOrgOwnerPermissions] = useState([]);
    const [errorMessage, setErrorMessage] = useState();
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const userAuthService = new UserAuthService();
    const errorHandler = new ErrorHandler();
    const { activeUserId, activeUser } = useReduxActiveUser();
    const className = 'deleteAccountPage';

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        try {
            let userOwnerPermissionsObj = await userAuthService.getUserOwnerPermissionList(activeUserId);
            userOwnerPermissionsObj = JSON.parse(userOwnerPermissionsObj.body);
            setProjectOwnerPermissions(userOwnerPermissionsObj.projects);
            setTeamOwnerPermissions(userOwnerPermissionsObj.teams);
            setOrgOwnerPermissions(userOwnerPermissionsObj.organizations);
            if (
                userOwnerPermissionsObj.projects.length !== 0 ||
                userOwnerPermissionsObj.teams.length !== 0 ||
                userOwnerPermissionsObj.organizations.length !== 0
            ) {
                setDeleteOptionDisabled(true);
            } else {
                setDeleteOptionDisabled(false);
            }
        } catch (err) {
            errorHandler.handleError(err);
        }
    };

    const handleDeleteTextChange = (e) => {
        let sanitizedText = DOMPurify.sanitize(e.target.value);
        setDeleteText(sanitizedText);
        setShowErrorMessage(false);
    };

    const handleEmailTextChange = (e) => {
        let sanitizedText = DOMPurify.sanitize(e.target.value);
        setEmailId(sanitizedText);
        setShowErrorMessage(false);
    };

    const handleDeleteClick = async () => {
        if (deleteText === 'Delete my account') {
            if (emailId === activeUser.email) {
                setShowConfirmation(true);
            } else {
                setShowErrorMessage(true);
                setErrorMessage(
                    'This email address does not match your account. Please enter the correct email address.'
                );
            }
        }
    };

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);
            let deleteMap = {
                id: activeUserId,
                emailId: emailId,
            };
            await userAuthService.deleteUser(deleteMap);
            userAuthService.logout();
        } catch (err) {
            setShowErrorMessage(true);
            setErrorMessage('Failed to delete account. Please try again.');
            setIsDeleting(false);
        }
    };

    const isFormValid = deleteText === 'Delete my account' && emailId.length > 2 && !deleteOptionDisabled;
    const totalOwnerItems = projectOwnerPermissions?.length + teamOwnerPermissions?.length + orgOwnerPermissions?.length;

    return (
        <div className={className}>
            <div className={`${className}__container`}>
                {/* Header */}
                <div className={`${className}__header`}>
                    <div className={`${className}__header__icon`}>
                        <IoWarning />
                    </div>
                    <div className={`${className}__header__content`}>
                        <h1 className={`${className}__title`}>Delete Account</h1>
                        <p className={`${className}__subtitle`}>
                            Permanently remove your account and all associated data
                        </p>
                    </div>
                </div>

                <div className={`${className}__content`}>
                    {/* Ownership Blockers */}
                    {deleteOptionDisabled && (
                        <div className={`${className}__blocker`}>
                            <div className={`${className}__blocker__header`}>
                                <IoAlertCircle />
                                <div>
                                    <h3>Account Deletion Blocked</h3>
                                    <p>
                                        You cannot delete your account while you are the owner of {totalOwnerItems}{' '}
                                        {totalOwnerItems === 1 ? 'item' : 'items'}
                                    </p>
                                </div>
                            </div>

                            {projectOwnerPermissions.length > 0 && (
                                <div className={`${className}__ownership-section`}>
                                    <div className={`${className}__ownership-section__header`}>
                                        <IoFolderOpen />
                                        <h4>
                                            Projects <Chip label={projectOwnerPermissions.length} size="small" color="primary" />
                                        </h4>
                                    </div>
                                    <div className={`${className}__ownership-list`}>
                                        {projectOwnerPermissions.map((project, index) => (
                                            <div key={index} className={`${className}__ownership-item`}>
                                                <div className={`${className}__ownership-item__tag`}>{project.tag}</div>
                                                <div className={`${className}__ownership-item__name`}>{project.title}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {teamOwnerPermissions.length > 0 && (
                                <div className={`${className}__ownership-section`}>
                                    <div className={`${className}__ownership-section__header`}>
                                        <IoPeople />
                                        <h4>
                                            Teams <Chip label={teamOwnerPermissions.length} size="small" color="primary" />
                                        </h4>
                                    </div>
                                    <div className={`${className}__ownership-list`}>
                                        {teamOwnerPermissions.map((team, index) => (
                                            <div key={index} className={`${className}__ownership-item`}>
                                                <div className={`${className}__ownership-item__tag`}>{team.tag}</div>
                                                <div className={`${className}__ownership-item__name`}>{team.title}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {orgOwnerPermissions.length > 0 && (
                                <div className={`${className}__ownership-section`}>
                                    <div className={`${className}__ownership-section__header`}>
                                        <IoBusiness />
                                        <h4>
                                            Organizations <Chip label={orgOwnerPermissions.length} size="small" color="primary" />
                                        </h4>
                                    </div>
                                    <div className={`${className}__ownership-list`}>
                                        {orgOwnerPermissions.map((org, index) => (
                                            <div key={index} className={`${className}__ownership-item`}>
                                                <div className={`${className}__ownership-item__tag`}>{org.tag}</div>
                                                <div className={`${className}__ownership-item__name`}>{org.title}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className={`${className}__blocker__action`}>
                                <IoAlertCircle />
                                <p>Please delete these items or transfer ownership to another user before proceeding.</p>
                            </div>
                        </div>
                    )}

                    {/* Warning Notice */}
                    <div className={`${className}__warning ${deleteOptionDisabled ? `${className}__warning--disabled` : ''}`}>
                        <div className={`${className}__warning__icon`}>
                            <IoWarning />
                        </div>
                        <div className={`${className}__warning__content`}>
                            <h3>Warning: This action cannot be undone</h3>
                            <ul>
                                <li>All your personal data will be permanently deleted</li>
                                <li>You will lose access to all projects, teams, and organizations</li>
                                <li>Any active subscriptions will be cancelled</li>
                                <li>This action is irreversible and cannot be recovered</li>
                            </ul>
                        </div>
                    </div>

                    {/* Confirmation Form */}
                    <div className={`${className}__form ${deleteOptionDisabled ? `${className}__form--disabled` : ''}`}>
                        <div className={`${className}__form__instructions`}>
                            <p>
                                To confirm deletion, please type{' '}
                                <strong className={`${className}__form__instructions__phrase`}>Delete my account</strong>{' '}
                                and enter your registered email address below:
                            </p>
                        </div>

                        <div className={`${className}__form__field`}>
                            <label className={`${className}__form__label`}>Confirmation Phrase</label>
                            <TextField
                                placeholder="Delete my account"
                                value={deleteText}
                                onChange={handleDeleteTextChange}
                                disabled={deleteOptionDisabled}
                                variant="outlined"
                                fullWidth
                                error={deleteText !== '' && deleteText !== 'Delete my account'}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IoTrashBin className={`${className}__form__icon`} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: deleteText === 'Delete my account' && (
                                        <InputAdornment position="end">
                                            <IoCheckmarkCircle style={{ color: '#22c55e' }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: '12px',
                                        backgroundColor: 'var(--color-muiBackground)'
                                    },
                                }}
                            />
                            {deleteText !== '' && deleteText !== 'Delete my account' && (
                                <div className={`${className}__form__error`}>
                                    <IoCloseCircle />
                                    <span>Please type exactly: "Delete my account"</span>
                                </div>
                            )}
                        </div>

                        <div className={`${className}__form__field`}>
                            <label className={`${className}__form__label`}>Email Address</label>
                            <TextField
                                placeholder={activeUser?.email || 'your@email.com'}
                                value={emailId}
                                onChange={handleEmailTextChange}
                                disabled={deleteOptionDisabled}
                                variant="outlined"
                                fullWidth
                                error={showErrorMessage}
                                type="email"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IoMail className={`${className}__form__icon`} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: emailId === activeUser?.email && (
                                        <InputAdornment position="end">
                                            <IoCheckmarkCircle style={{ color: '#22c55e' }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: '12px',
                                        backgroundColor: 'var(--color-muiBackground)'
                                    },
                                }}
                            />
                            {showErrorMessage && (
                                <div className={`${className}__form__error`}>
                                    <IoCloseCircle />
                                    <span>{errorMessage}</span>
                                </div>
                            )}
                        </div>

                        <div className={`${className}__form__actions`}>
                            <button
                                type="button"
                                className={`${className}__form__delete-btn`}
                                onClick={handleDeleteClick}
                                disabled={!isFormValid || isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <div className={`${className}__spinner`}></div>
                                        Deleting Account...
                                    </>
                                ) : (
                                    <>
                                        <IoTrashBin />
                                        Delete My Account Permanently
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Final Confirmation Modal */}
                {showConfirmation && (
                    <div className={`${className}__modal-overlay`}>
                        <div className={`${className}__modal`}>
                            <div className={`${className}__modal__header`}>
                                <IoWarning />
                                <h3>Final Confirmation</h3>
                            </div>
                            <div className={`${className}__modal__content`}>
                                <p>Are you absolutely sure you want to delete your account?</p>
                                <p className={`${className}__modal__warning`}>
                                    This will permanently delete all your data and cannot be recovered.
                                </p>
                            </div>
                            <div className={`${className}__modal__actions`}>
                                <button
                                    className={`${className}__modal__cancel`}
                                    onClick={() => setShowConfirmation(false)}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={`${className}__modal__confirm`}
                                    onClick={handleConfirmDelete}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? (
                                        <>
                                            <div className={`${className}__spinner`}></div>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <IoTrashBin />
                                            Yes, Delete Forever
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
